import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Collapse,
} from '@mui/material';
import {
  Dashboard,
  Folder,
  Category,
  Article,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  LocalHospital,
  Group,
  Campaign,
  History,
  AccountBalanceWallet,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    management: true,
  });
  const navigate = useNavigate();
  const location = useLocation();

  const handleSectionToggle = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const overviewItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
      { text: 'Categories', icon: <Category />, path: '/categories' },
      { text: 'Banners', icon: <Campaign />, path: '/banners' },
      { text: 'Blogs', icon: <Article />, path: '/blogs' },
      { text: 'Policies', icon: <PolicyIcon />, path: '/policies' },
      // { text: 'Analytics', icon: <Analytics />, path: '/analytics' },
    { text: 'Doctors', icon: <LocalHospital />, path: '/doctors' },
    { text: 'Patients', icon: <Group />, path: '/patients' },
    { text: 'Appointments', icon: <Folder />, path: '/appointments' },
    { text: 'Withdrawals', icon: <AccountBalanceWallet />, path: '/withdrawals' },
    { text: 'Activity Logs', icon: <History />, path: '/activity-logs' },
  ];

//   const managementItems = [
//     {
//       text: 'User',
//       icon: <Person />,
//       items: [
//         { text: 'Profile', path: '/user/profile' },
//         { text: 'Cards', path: '/user/cards' },
//         { text: 'List', path: '/patients' },
//         { text: 'Create', path: '/user/create' },
//         { text: 'Edit', path: '/user/edit' },
//         { text: 'Account', path: '/user/account' },
//       ]
//     },
//     {
//       text: 'Invoice',
//       icon: <Article />,
//       items: [
//         { text: 'List', path: '/invoice/list' },
//         { text: 'Details', path: '/invoice/details' },
//         { text: 'Create', path: '/invoice/create' },
//         { text: 'Edit', path: '/invoice/edit' },
//       ]
//     },
   
//   ];

  const renderNavItem = (item: any, level = 0) => {
    const isSelected = location.pathname === item.path;
    const hasChildren = item.items && item.items.length > 0;
    const isExpanded = expandedSections[item.text.toLowerCase()];

    if (hasChildren) {
      return (
        <Box key={item.text}>
          <ListItem disablePadding sx={{ px: 1, mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleSectionToggle(item.text.toLowerCase())}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                pl: level * 2 + 1,
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  color: 'white',
                },
                '& .MuiListItemIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '&:hover .MuiListItemIcon-root': {
                  color: 'white',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center' }}>
                {item.icon}
              </ListItemIcon>
              {isOpen && (
                <>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: 'inherit',
                    }}
                  />
                  {isExpanded ? <ExpandLess sx={{ color: 'inherit' }} /> : <ExpandMore sx={{ color: 'inherit' }} />}
                </>
              )}
            </ListItemButton>
          </ListItem>
          <Collapse in={isExpanded && isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.items.map((subItem: any) => (
                <ListItem key={subItem.text} disablePadding sx={{ px: 1, mb: 0.5 }}>
                  <ListItemButton
                    selected={location.pathname === subItem.path}
                    onClick={() => navigate(subItem.path)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      pl: level * 2 + 3,
                      color: 'rgba(255, 255, 255, 0.6)',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 167, 111, 0.16)',
                        color: '#00A76F',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 167, 111, 0.24)',
                        },
                        '& .MuiListItemIcon-root': {
                          color: '#00A76F',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        color: 'white',
                      },
                    }}
                  >
                    <ListItemText 
                      primary={subItem.text} 
                      primaryTypographyProps={{
                        fontWeight: location.pathname === subItem.path ? 600 : 400,
                        fontSize: '0.875rem',
                        color: 'inherit',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      );
    }

    return (
      <ListItem key={item.text} disablePadding sx={{ px: 1, mb: 0.5 }}>
        <ListItemButton
          selected={isSelected}
          onClick={() => navigate(item.path)}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            pl: level * 2 + 1,
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 167, 111, 0.16)',
              color: '#00A76F',
              '&:hover': {
                backgroundColor: 'rgba(0, 167, 111, 0.24)',
              },
              '& .MuiListItemIcon-root': {
                color: '#00A76F',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              color: 'white',
            },
            '& .MuiListItemIcon-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '&:hover .MuiListItemIcon-root': {
              color: 'white',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center' }}>
            {item.badge ? (
              <Badge badgeContent={item.badge} color="error">
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
          {isOpen && (
            <ListItemText 
              primary={item.text} 
              primaryTypographyProps={{
                fontWeight: isSelected ? 600 : 400,
                fontSize: '0.875rem',
                color: 'inherit',
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: 'block',
        '& .MuiDrawer-paper': { 
          boxSizing: 'border-box', 
          width: isOpen ? 240 : 64,
          backgroundColor: '#161C24',
          borderRight: 'none',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#161C24' }}>
        {/* Logo Section */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                background: 'linear-gradient(135deg, #00A76F 0%, #5BE49B 100%)',
                borderRadius: '8px',
                mr: isOpen ? 2 : 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                P
              </Typography>
            </Box>
            {isOpen && (
              <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, color: 'white' }}>
                Panchkarma
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onToggle}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            {isOpen ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

        {/* Navigation Sections */}
        <Box sx={{ flexGrow: 1, py: 1 }}>
          {/* OVERVIEW Section */}
          {/* <Box sx={{ px: 2, py: 1 }}>
            {isOpen && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem',
                }}
              >
                OVERVIEW
              </Typography>
            )}
          </Box> */}
          <List sx={{ px: 1 }}>
            {overviewItems.map((item) => renderNavItem(item))}
          </List>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)', my: 1 }} />

          {/* MANAGEMENT Section */}
          {/* <Box sx={{ px: 2, py: 1 }}>
            {isOpen && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem',
                }}
              >
                MANAGEMENT
              </Typography>
            )}
          </Box>
          <List sx={{ px: 1 }}>
            {managementItems.map((item) => renderNavItem(item))}
          </List> */}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
